import React from 'react';
import {
  Button, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@parsina/framework';

import { useTranslation } from '../i18n';
import { SESSION_ENDED_EVENT } from '../lib/api';
import { clearTokens, redirectToLogin } from '../lib/auth';

/** Where to come back to after re-authenticating. */
const RETURN_KEY = 'post_login_return';

/**
 * The session has ended, and the app is still on screen.
 *
 * DESIGN-CONCEPT §13.1: a session ending is an EVENT, not a navigation. The
 * previous behaviour was a redirect from inside the GraphQL client, which is a
 * full-document navigation — React unmounts and everything unsaved goes with it.
 * On this panel that means a typed suspension reason or a half-filled manual
 * activation, both of which are audited writes an operator would have to
 * reconstruct from memory.
 *
 * So the dialog sits OVER the work instead. It is deliberately:
 *
 *   • NON-DISMISSIBLE — no ✕, no overlay-click, no Esc. Every request from here
 *     on will fail, and a panel that looks alive while nothing works is worse
 *     than one that says so. `role="alertdialog"` is the shape for a message
 *     that must be answered rather than read.
 *   • honest about the cost — it says the open forms will be lost, because they
 *     will be, and an operator who knows that can copy a reason out first.
 *
 * The URL is stored before leaving so re-login returns to the same screen rather
 * than to the dashboard.
 */
export default function SessionDialog() {
  const { t } = useTranslation();
  const [ended, setEnded] = React.useState(false);

  React.useEffect(() => {
    const onEnded = () => setEnded(true);
    window.addEventListener(SESSION_ENDED_EVENT, onEnded);
    return () => window.removeEventListener(SESSION_ENDED_EVENT, onEnded);
  }, []);

  if (!ended) return null;

  const signIn = () => {
    try {
      sessionStorage.setItem(RETURN_KEY, window.location.pathname + window.location.search);
    } catch { /* storage off — the operator lands on the dashboard instead */ }
    clearTokens();
    redirectToLogin();
  };

  return (
    <Dialog open modal>
      <DialogContent
        role="alertdialog"
        showClose={false}
        size="sm"
        // Nothing here closes the dialog: the session is gone and every action
        // behind it would fail. The way out is forward.
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        data-testid="session-dialog"
      >
        <DialogHeader><DialogTitle>{t('auth.session.title')}</DialogTitle></DialogHeader>
        <p className="text-sm leading-6 text-muted-foreground">{t('auth.session.body')}</p>
        <DialogFooter className="mt-6">
          <Button onClick={signIn} data-testid="session-dialog-signin">
            {t('auth.session.action')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * The URL to restore after a re-login, consumed once.
 *
 * Read by `AuthGate` after the code exchange. Consuming it means a later manual
 * sign-in does not silently teleport the operator to wherever they were days ago.
 */
export function takeReturnUrl() {
  try {
    const url = sessionStorage.getItem(RETURN_KEY);
    sessionStorage.removeItem(RETURN_KEY);
    return url || null;
  } catch {
    return null;
  }
}
